import type { Branding } from "./types";

/**
 * Mix a hex colour toward white by `whiteRatio` (0..1). Returns a #rrggbb string,
 * or null if the input isn't a parseable #rgb / #rrggbb hex. Used to derive a
 * dark-mode-legible variant of the tenant's (often mid-tone) brand colour for
 * brand-coloured TEXT — done in JS so we don't depend on CSS color-mix support.
 */
function lightenHex(hex: string, whiteRatio: number): string | null {
  const m = hex.trim().replace(/^#/, "");
  const full = m.length === 3 ? m.split("").map((c) => c + c).join("") : m;
  if (full.length !== 6 || /[^0-9a-fA-F]/.test(full)) return null;
  const mix = (c: number) => Math.round(c * (1 - whiteRatio) + 255 * whiteRatio);
  const ch = (i: number) => mix(parseInt(full.slice(i, i + 2), 16)).toString(16).padStart(2, "0");
  return `#${ch(0)}${ch(2)}${ch(4)}`;
}

/**
 * Apply a tenant's branding as CSS custom properties on :root so the whole app
 * uses it live — primary buttons (`--brand-primary`) and accent elements like
 * links/focus rings/active states (`--accent`, `--brand`, `--brand-1`). Blank or
 * null values CLEAR the override, falling back to the built-in defaults.
 *
 * Called on app load (Layout) and right after saving in Settings → Branding, so
 * changes take effect without a reload.
 */
export function applyBranding(b: Partial<Branding> | null | undefined): void {
  const root = document.documentElement;
  const set = (name: string, val: string | null | undefined) => {
    const v = typeof val === "string" ? val.trim() : "";
    if (v) root.style.setProperty(name, v);
    else root.style.removeProperty(name);
  };

  const primary = b?.primary_color ?? null;
  // Primary/accent colour drives primary buttons + accent (links/focus/active).
  set("--brand-primary", primary);
  set("--accent", primary);
  set("--brand", primary);
  set("--brand-1", primary);
  set("--brand-secondary", b?.secondary_color ?? null);
  // Dark-mode link/chip text: a lightened tint of the brand so it stays legible
  // on the dark surface (the raw mid-tone brand is too dark / too saturated).
  // Cleared when there's no custom brand → CSS falls back to the default emerald.
  set("--brand-text-dark", primary ? lightenHex(primary, 0.62) : null);

  const favicon = b?.favicon_url?.trim();
  if (favicon) {
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = favicon;
  }
}
